import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { en } from '../../translations/en';
import { useCollections } from '../../hooks/useCollections';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { getCatalogDisplayName } from '../../utils/stringHelpers';
import {
  createDelegationInvitation,
  fetchSentInvitations,
  revokeInvitation,
  fetchGrantedDelegations,
  revokeDelegation,
  updateDelegation,
  SentInvitation,
  ActiveDelegation,
} from '../../utils/delegationApi';
import { RevokeConfirmDialog } from './RevokeConfirmDialog';
import { extractAxiosErrorMessage } from '../../utils/errorUtils';

const t = en.settings.delegates;

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Sentinel value representing access to all catalog items (no collection scoping). */
export const ALL_ITEMS_COLLECTION_ID = 'all';

/**
 * Scope-dropdown value prefix for a "This Catalog" option (GH#1219). The
 * dropdown unifies three scope kinds into one <select>:
 *   - ``'all'``                  → catalog-wide, all owned catalogs
 *   - ``'CATALOG#<LL-NXX>'``     → catalog-wide, narrowed to one owned catalog
 *   - ``'<collection-uuid>'``    → album-scoped
 * The prefix keeps catalog ali-ids from colliding with album collection IDs.
 */
const CATALOG_SCOPE_PREFIX = 'CATALOG#';

/** Encode an owned catalog's ali-id as a scope-dropdown value. */
const encodeCatalogScope = (catalogAliId: string): string => `${CATALOG_SCOPE_PREFIX}${catalogAliId}`;

/**
 * Decode a scope-dropdown value into the API request shape. Returns the
 * ``collectionId`` and optional ``catalogAliId`` to send.
 */
const decodeScopeValue = (value: string): { collectionId: string; catalogAliId?: string } => {
  if (value.startsWith(CATALOG_SCOPE_PREFIX)) {
    return { collectionId: ALL_ITEMS_COLLECTION_ID, catalogAliId: value.slice(CATALOG_SCOPE_PREFIX.length) };
  }
  return { collectionId: value };
};

interface RevokeTarget {
  type: 'invitation' | 'delegation';
  id: string;
  label: string;
}

const getPermissionLabel = (permission: string): string => {
  const labels: Record<string, string> = {
    view: t.permissions.view,
    full: t.permissions.full,
  };
  return labels[permission] || permission;
};

/**
 * Read-only access summary for a delegate row (permission level + optional
 * CV-edit). Rendered as plain text — deliberately NOT MUI Chips/pills, which
 * read as tappable buttons. The permission is emphasised and CV-edit is set
 * off by a middot separator so "View" and "CV edit" don't run together.
 * Shared by the pending-invitations and active-delegations lists.
 */
const AccessLabels = ({ permissions, canEditCv }: { permissions: string; canEditCv?: boolean }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Typography variant="body2" component="span" color="text.primary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
      {getPermissionLabel(permissions)}
    </Typography>
    {canEditCv && (
      <>
        <Typography variant="body2" component="span" color="text.disabled" aria-hidden="true">
          ·
        </Typography>
        <Typography variant="body2" component="span" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {t.cvEditChip}
        </Typography>
      </>
    )}
  </Box>
);

const getApiErrorMessage = (err: unknown): string => {
  return extractAxiosErrorMessage(err, 'An unexpected error occurred');
};

export const DelegatesTab: React.FC = () => {
  const { collections } = useCollections();
  // Owned catalogs (primary + add-ons) for the "This Catalog" scope group
  // (GH#1219). Sourced from the subscription-backed list synthesized in
  // ActiveCatalogContext (reused from GH#1220's fetchOwnedCatalogs).
  const { ownedCatalogs: ownedCatalogScopes } = useActiveCatalog();

  // Only show ALBUMS the current user owns (not collections they're a member of)
  const ownedCatalogs = collections.filter(c => c.role === 'owner');

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [inviteCollectionId, setInviteCollectionId] = useState(ALL_ITEMS_COLLECTION_ID);
  const [invitePermissions, setInvitePermissions] = useState('view');
  const [inviteCanEditCv, setInviteCanEditCv] = useState(false);
  const [inviteExpiryDays, setInviteExpiryDays] = useState(7);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Data lists state
  const [sentInvitations, setSentInvitations] = useState<SentInvitation[]>([]);
  const [activeDelegations, setActiveDelegations] = useState<ActiveDelegation[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Revoke dialog state
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Edit delegation dialog state — change collection scope, permission level,
  // CV-edit capability, and expiry from one dialog opened off the active list.
  const [editTarget, setEditTarget] = useState<ActiveDelegation | null>(null);
  const [editCollectionId, setEditCollectionId] = useState(ALL_ITEMS_COLLECTION_ID);
  const [editPermissions, setEditPermissions] = useState('view');
  const [editCanEditCv, setEditCanEditCv] = useState(false);
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setFetchError(null);
      const [invResult, delResult] = await Promise.all([
        fetchSentInvitations(),
        fetchGrantedDelegations(),
      ]);
      setSentInvitations(invResult.invitations);
      setActiveDelegations(delResult.delegations);
    } catch (err) {
      setFetchError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resolveCatalogScopeName = (catalogAliId: string): string => {
    const owned = ownedCatalogScopes.find(c => c.catalogAliId === catalogAliId);
    if (owned) {
      return owned.isPrimary ? 'Main Catalog' : getCatalogDisplayName(owned.creatorName);
    }
    // Fall back to the raw ali-id when the owned-catalog list hasn't resolved.
    return catalogAliId;
  };

  const resolveCollectionName = (collectionId: string, catalogAliId?: string): string => {
    // GH#1219: a catalog-wide grant narrowed to a single owned catalog shows
    // that catalog's name rather than the generic "All Items".
    if (catalogAliId) return resolveCatalogScopeName(catalogAliId);
    if (collectionId === ALL_ITEMS_COLLECTION_ID) return 'All Items';
    const collection = collections.find(c => c.collectionId === collectionId);
    if (!collection) return collectionId;
    return collection.isDefault ? 'Main Catalog' : getCatalogDisplayName(collection.name);
  };

  const resetInviteForm = () => {
    setInviteEmail('');
    setInviteeName('');
    setInviteCollectionId(ALL_ITEMS_COLLECTION_ID);
    setInvitePermissions('view');
    setInviteCanEditCv(false);
    setInviteExpiryDays(7);
    setInviteMessage('');
  };

  const handleSendInvitation = async () => {
    setInviteError(null);
    setInviteSuccess(false);

    if (!EMAIL_REGEX.test(inviteEmail)) {
      setInviteError(t.invite.errorEmail);
      return;
    }
    if (!inviteCollectionId) {
      setInviteError(t.invite.errorCollection);
      return;
    }
    if (inviteExpiryDays < 1 || inviteExpiryDays > 90) {
      setInviteError(t.invite.errorExpiry);
      return;
    }

    try {
      setInviteSending(true);
      // GH#1219: a "This Catalog" option decodes to collectionId='all' +
      // catalogAliId=<LL-NXX>; an album or "All Items" decodes to a plain
      // collectionId with no catalogAliId.
      const scope = decodeScopeValue(inviteCollectionId);
      await createDelegationInvitation({
        inviteeEmail: inviteEmail,
        ...(inviteeName ? { inviteeName } : {}),
        collectionId: scope.collectionId,
        ...(scope.catalogAliId ? { catalogAliId: scope.catalogAliId } : {}),
        permissions: invitePermissions,
        canEditCv: inviteCanEditCv,
        expiresInDays: inviteExpiryDays,
        ...(inviteMessage ? { message: inviteMessage } : {}),
      });
      resetInviteForm();
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 5000);
      await loadData();
    } catch (err) {
      setInviteError(getApiErrorMessage(err));
    } finally {
      setInviteSending(false);
    }
  };

  const handleEditOpen = (del: ActiveDelegation) => {
    setEditTarget(del);
    // GH#1219: a catalog-narrowed grant maps back to its "This Catalog"
    // dropdown value so the current scope is reflected on open.
    setEditCollectionId(del.catalogAliId ? encodeCatalogScope(del.catalogAliId) : del.collectionId);
    setEditPermissions(del.permissions);
    setEditCanEditCv(!!del.canEditCv);
    setEditExpiryDate(del.expiresAt.split('T')[0]);
    setEditError(null);
  };

  const handleEditClose = () => {
    setEditTarget(null);
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;

    try {
      setEditSubmitting(true);
      setEditError(null);

      // Send all editable fields. The backend re-scopes (revoke + recreate)
      // when collectionId changed and does an in-place update otherwise, so
      // passing collectionId unconditionally is safe.
      //
      // Only send expiresAt when the DATE actually changed. The date input is
      // day-granular, so resending `${date}T00:00:00Z` on an unrelated edit
      // (permission/collection/CV) would truncate any time component the
      // stored expiry has and silently shorten the delegation by up to a day.
      const originalDate = editTarget.expiresAt.split('T')[0];
      const expiryChanged = editExpiryDate !== originalDate;
      const id = `${editTarget.delegatorAliId}#${editTarget.delegationKey}`;
      // GH#1219: decode the unified scope value back to collectionId +
      // optional catalogAliId.
      const scope = decodeScopeValue(editCollectionId);
      await updateDelegation(id, {
        collectionId: scope.collectionId,
        ...(scope.catalogAliId ? { catalogAliId: scope.catalogAliId } : {}),
        permissions: editPermissions,
        canEditCv: editCanEditCv,
        ...(expiryChanged ? { expiresAt: `${editExpiryDate}T00:00:00Z` } : {}),
      });

      setEditTarget(null);
      await loadData();
    } catch (err) {
      setEditError(getApiErrorMessage(err));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;

    try {
      setRevokeSubmitting(true);
      setRevokeError(null);

      if (revokeTarget.type === 'invitation') {
        await revokeInvitation(revokeTarget.id);
      } else {
        await revokeDelegation(revokeTarget.id);
      }

      setRevokeTarget(null);
      await loadData();
    } catch (err) {
      const error = err as { response?: { status?: number } };
      // Treat 404 as success (item already gone)
      if (error.response?.status === 404) {
        setRevokeTarget(null);
        await loadData();
        return;
      }
      setRevokeError(getApiErrorMessage(err));
    } finally {
      setRevokeSubmitting(false);
    }
  };

  const pendingInvitations = sentInvitations.filter(inv => inv.status === 'pending');
  const hasNoData = pendingInvitations.length === 0 && activeDelegations.length === 0;

  const sortedCatalogs = [...ownedCatalogs].sort(
    (a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)
  );

  // GH#1219: owned catalogs (primary + add-ons) for the "This Catalog" scope
  // group, primary first. Only meaningful when the user owns more than one
  // catalog — a single-catalog user gains nothing from per-catalog narrowing,
  // so the group is hidden to avoid a redundant duplicate of "All Items".
  const sortedCatalogScopes = [...(ownedCatalogScopes ?? [])].sort(
    (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
  );
  const showCatalogScopeGroup = sortedCatalogScopes.length > 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>{t.title}</Typography>
      <Typography variant="body2" paragraph color="text.secondary">
        {t.description}
      </Typography>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadData}>Retry</Button>
        }>
          {fetchError}
        </Alert>
      )}

      {/* Section 1: Invite Form */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>{t.invite.title}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500, mb: 4 }}>
        <TextField
          label={t.invite.email}
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          fullWidth
          variant="outlined"
          required
          helperText={t.invite.emailHelper}
          disabled={inviteSending}
        />
        <TextField
          label={t.invite.delegateName}
          value={inviteeName}
          onChange={(e) => setInviteeName(e.target.value)}
          fullWidth
          variant="outlined"
          inputProps={{ maxLength: 200 }}
          helperText={t.invite.delegateNameHelper}
          disabled={inviteSending}
        />
        <TextField
          select
          label={t.invite.collection}
          value={inviteCollectionId}
          onChange={(e) => setInviteCollectionId(e.target.value)}
          fullWidth
          variant="outlined"
          required
          helperText={t.invite.collectionHelper}
          disabled={inviteSending}
        >
          <MenuItem key={ALL_ITEMS_COLLECTION_ID} value={ALL_ITEMS_COLLECTION_ID}>
            All Items
          </MenuItem>
          {showCatalogScopeGroup &&
            sortedCatalogScopes.map(cat => (
              <MenuItem key={encodeCatalogScope(cat.catalogAliId)} value={encodeCatalogScope(cat.catalogAliId)}>
                {`This Catalog: ${cat.isPrimary ? 'Main Catalog' : getCatalogDisplayName(cat.creatorName)}`}
              </MenuItem>
            ))}
          {sortedCatalogs.map(catalog => (
            <MenuItem key={catalog.collectionId} value={catalog.collectionId}>
              {catalog.isDefault ? 'Main Catalog' : getCatalogDisplayName(catalog.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t.invite.permission}
          value={invitePermissions}
          onChange={(e) => setInvitePermissions(e.target.value)}
          fullWidth
          variant="outlined"
          helperText={t.invite.permissionHelper}
          disabled={inviteSending}
        >
          <MenuItem value="view">{t.permissions.view}</MenuItem>
          <MenuItem value="full">{t.permissions.full}</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={inviteCanEditCv}
              onChange={(e) => setInviteCanEditCv(e.target.checked)}
              disabled={inviteSending}
            />
          }
          label={
            <Box>
              <Typography variant="body2">{t.invite.canEditCv}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t.invite.canEditCvHelper}
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start' }}
        />
        <TextField
          label={t.invite.expiry}
          type="number"
          value={inviteExpiryDays}
          onChange={(e) => setInviteExpiryDays(Number(e.target.value))}
          fullWidth
          variant="outlined"
          inputProps={{ min: 1, max: 90 }}
          helperText={t.invite.expiryHelper}
          disabled={inviteSending}
        />
        <TextField
          label={t.invite.message}
          value={inviteMessage}
          onChange={(e) => setInviteMessage(e.target.value)}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          inputProps={{ maxLength: 500 }}
          helperText={t.invite.messageHelper}
          disabled={inviteSending}
        />
        {inviteSuccess && (
          <Alert severity="success">{t.invite.success}</Alert>
        )}
        {inviteError && (
          <Alert severity="error">{inviteError}</Alert>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendInvitation}
          disabled={inviteSending || !inviteEmail || !inviteCollectionId}
          startIcon={inviteSending ? <CircularProgress size={16} /> : undefined}
        >
          {inviteSending ? t.invite.sending : t.invite.send}
        </Button>
      </Box>

      {/* Section 2: Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>{t.pending.title}</Typography>
          <List>
            {pendingInvitations.map(inv => (
              <ListItem
                key={inv.invitationToken}
                sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}
              >
                <ListItemText
                  primary={
                    <Box component="span">
                      {inv.inviteeName && (
                        <Typography variant="body1" component="span">
                          {inv.inviteeName}
                        </Typography>
                      )}
                      <Typography
                        variant={inv.inviteeName ? 'body2' : 'body1'}
                        component={inv.inviteeName ? 'div' : 'span'}
                        color={inv.inviteeName ? 'text.secondary' : 'text.primary'}
                      >
                        {inv.inviteeEmail}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      <Typography variant="body2" component="span" color="text.secondary">
                        {inv.catalogAliId
                          ? resolveCollectionName(inv.collectionId, inv.catalogAliId)
                          : inv.collectionName || resolveCollectionName(inv.collectionId)}
                      </Typography>
                      <Typography variant="body2" component="span" color="text.secondary">
                        Expires: {new Date(inv.expiresAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AccessLabels permissions={inv.permissions} canEditCv={inv.canEditCv} />
                  {/* Rendered inline (not in ListItemSecondaryAction, which is
                      absolutely positioned and would overlap the labels). */}
                  <IconButton
                    aria-label="revoke"
                    onClick={() => setRevokeTarget({
                      type: 'invitation',
                      id: inv.invitationToken,
                      label: inv.inviteeEmail,
                    })}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Section 3: Active Delegations */}
      {activeDelegations.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>{t.active.title}</Typography>
          <List>
            {activeDelegations.map(del => (
              <ListItem
                key={del.delegationKey}
                sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}
              >
                <ListItemText
                  primary={
                    <Box component="span">
                      {del.delegateEmail}
                      {del.delegateAliId && (
                        <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                          ({del.delegateAliId})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      <Typography variant="body2" component="span" color="text.secondary">
                        {resolveCollectionName(del.collectionId, del.catalogAliId)}
                      </Typography>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" component="span" color="text.secondary">
                          Expires: {new Date(del.expiresAt).toLocaleDateString()}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="edit delegation"
                          onClick={() => handleEditOpen(del)}
                          sx={{ p: 0.25 }}
                        >
                          <Edit fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AccessLabels permissions={del.permissions} canEditCv={del.canEditCv} />
                  {/* Rendered inline (not in ListItemSecondaryAction, which is
                      absolutely positioned and would overlap the labels). */}
                  <IconButton
                    aria-label="revoke"
                    onClick={() => setRevokeTarget({
                      type: 'delegation',
                      id: `${del.delegatorAliId}#${del.delegationKey}`,
                      label: del.delegateEmail,
                    })}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Empty state */}
      {!loading && !fetchError && hasNoData && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t.empty}
        </Typography>
      )}

      {/* Revoke confirmation dialog */}
      <RevokeConfirmDialog
        open={revokeTarget !== null}
        onClose={() => { setRevokeTarget(null); setRevokeError(null); }}
        onConfirm={handleRevokeConfirm}
        isSubmitting={revokeSubmitting}
        error={revokeError}
        title={revokeTarget?.type === 'invitation' ? t.revoke.invitationTitle : t.revoke.delegationTitle}
        description={revokeTarget?.type === 'invitation' ? t.revoke.invitationDescription : t.revoke.delegationDescription}
        itemLabel={revokeTarget?.label || ''}
      />

      {/* Edit delegation dialog — collection scope, permission level,
          CV-edit capability, and expiry. */}
      <Dialog
        open={editTarget !== null}
        onClose={handleEditClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t.edit.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label={t.invite.collection}
              value={editCollectionId}
              onChange={(e) => setEditCollectionId(e.target.value)}
              fullWidth
              variant="outlined"
              helperText={t.edit.collectionHelper}
              disabled={editSubmitting}
            >
              <MenuItem key={ALL_ITEMS_COLLECTION_ID} value={ALL_ITEMS_COLLECTION_ID}>
                All Items
              </MenuItem>
              {showCatalogScopeGroup &&
                sortedCatalogScopes.map(cat => (
                  <MenuItem key={encodeCatalogScope(cat.catalogAliId)} value={encodeCatalogScope(cat.catalogAliId)}>
                    {`This Catalog: ${cat.isPrimary ? 'Main Catalog' : getCatalogDisplayName(cat.creatorName)}`}
                  </MenuItem>
                ))}
              {sortedCatalogs.map(catalog => (
                <MenuItem key={catalog.collectionId} value={catalog.collectionId}>
                  {catalog.isDefault ? 'Main Catalog' : getCatalogDisplayName(catalog.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t.invite.permission}
              value={editPermissions}
              onChange={(e) => setEditPermissions(e.target.value)}
              fullWidth
              variant="outlined"
              disabled={editSubmitting}
            >
              <MenuItem value="view">{t.permissions.view}</MenuItem>
              <MenuItem value="full">{t.permissions.full}</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editCanEditCv}
                  onChange={(e) => setEditCanEditCv(e.target.checked)}
                  disabled={editSubmitting}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t.invite.canEditCv}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.invite.canEditCvHelper}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
            <TextField
              label="New Expiration Date"
              type="date"
              value={editExpiryDate}
              onChange={(e) => setEditExpiryDate(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              disabled={editSubmitting}
            />
            {editError && (
              <Alert severity="error">{editError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEditClose}
            disabled={editSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSubmitting || !editCollectionId || !editExpiryDate}
            startIcon={editSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
